import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from "react";
import {
  Box,
  Grid,
  Divider,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useAppConfig } from "../../../../context/AppConfigContext";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";
import DeliveryCard from "./components/DeliveryCard";
import SetupCard from "./components/SetupCard";
import PickupCard from "./components/PickupCard";
import TotalsBar from "./components/TotalsBar";

/** ---------- utils ---------- */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function haversineKm(a, b) {
  if (!a || !b) return 0;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad((b.lat || 0) - (a.lat || 0));
  const dLng = toRad((b.lng || 0) - (a.lng || 0));
  const lat1 = toRad(a.lat || 0);
  const lat2 = toRad(b.lat || 0);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}

function getLocality(place) {
  const comps = place?.address_components || [];
  const hit =
    comps.find((c) => c.types?.includes("locality")) ||
    comps.find((c) => c.types?.includes("postal_town")) ||
    comps.find((c) => c.types?.includes("administrative_area_level_3"));
  return hit?.long_name || "";
}

function extractLatLngFromPlace(placeObj) {
  const loc = placeObj?.geometry?.location;
  const lat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
  const lng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;
  return {
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
}

function getTierCount(cake) {
  const arrCount = Array.isArray(cake?.tiers) ? cake.tiers.length : 0;
  if (arrCount) return arrCount;
  const key = String(cake?.compositeKey || ""); // e.g., "3T_TALL"
  const m = key.match(/^(\d+)T/i);
  return m ? Number(m[1]) : 0;
}

export default function DeliverySetupStep({ onValidityChange }) {
  const { config } = useAppConfig();
  const {
    state,
    setMethod,
    setAddress,
    setFulfillment,
    setPickupLocation,
    setWaiver,
  } = useWeddingWizard();

  // ---- config merge (delivery + origin, setup) ----
  const deliveryDefaults = useRef({
    enabled: true,
    baseKm: 15,
    baseFee: 25,
    perKmFee: 2,
    origin: {
      address: "16 Stavanger Dr, St. John's, NL A1A 5E8, Canada",
      lat: 47.61503602214265,
      lng: -52.71926481616616,
      city: "St. John's",
    },
    includedLocalities: ["St. John's", "Mount Pearl", "Paradise"],
  });

  const userDelivery = config?.weddings?.delivery || {};
  const dcfg = {
    ...deliveryDefaults.current,
    ...userDelivery,
    origin: {
      ...deliveryDefaults.current.origin,
      ...(userDelivery.origin || {}),
    },
  };

  // Pickup locations from AppConfig.delivery
  const pickupLocations = Array.isArray(config?.delivery?.pickupLocations)
    ? config.delivery.pickupLocations
    : [];

  const scfg = config?.weddings?.setup || {
    enabled: true,
    baseFee: 150,
    includes: [
      "Cake/dessert stands & plates",
      "Name tags & flavor labels",
      "Table cloths & risers",
      "Light florals/greenery (on request)",
      "Full on-site arrangement & styling",
    ],
    blurb:
      "We deliver, stage, and style your dessert table so it looks polished, practical, and guest-ready.",
  };

  // ---- local UI state for address confirmation & locality label ----
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [addressText, setAddressText] = useState("");
  const [locality, setLocality] = useState("");

  const originLatLng = { lat: dcfg.origin.lat, lng: dcfg.origin.lng };

  const method = state.fulfillment?.method || "delivery"; // 'delivery' | 'pickup'
  const setupSelected = !!state.fulfillment?.setupSelected;

  const tierCount = getTierCount(state.cake);
  const isThreePlus = tierCount >= 3;

  const distanceKm = Number(state.fulfillment?.address?.distanceKm) || 0;

  // delivery fee (raw)
  const deliveryFee = useMemo(() => {
    if (method !== "delivery" || !dcfg.enabled) return 0;
    const dist = Math.max(0, distanceKm);
    const baseKm = Number(dcfg.baseKm) || 0;
    const baseFee = Number(dcfg.baseFee) || 0;
    const perKm = Number(dcfg.perKmFee) || 0;
    if (dist <= 0) return 0;
    if (dist <= baseKm) return round2(baseFee);
    const extra = dist - baseKm;
    return round2(baseFee + extra * perKm);
  }, [method, dcfg, distanceKm]);

  // setup fee
  const setupFee = useMemo(() => {
    if (!scfg.enabled || !setupSelected) return 0;
    return round2(Number(scfg.baseFee) || 0);
  }, [setupSelected, scfg]);

  const isInsideIncludedLocality = useMemo(() => {
    const list = (dcfg.includedLocalities || []).map((s) =>
      String(s || "").toLowerCase()
    );
    return list.includes(String(locality || "").toLowerCase());
  }, [locality, dcfg.includedLocalities]);

  // Totals (setup includes base delivery; apply overage only)
  const baseDelivery = Number(dcfg.baseFee) || 25;
  const setupTravelOverage = useMemo(() => {
    if (!setupSelected || !scfg.enabled) return 0;
    if (isInsideIncludedLocality) return 0;
    return Math.max(0, round2(deliveryFee - baseDelivery));
  }, [
    setupSelected,
    scfg.enabled,
    isInsideIncludedLocality,
    deliveryFee,
    baseDelivery,
  ]);

  const billedDelivery = useMemo(() => {
    if (setupSelected) return 0; // included in setup
    if (method === "pickup") return 0;
    return deliveryFee;
  }, [setupSelected, deliveryFee, method]);

  const total = round2(setupFee + setupTravelOverage + billedDelivery);

  // validity rules
  const needsAddress = method === "delivery" || setupSelected;
  const pickupLocation = state.fulfillment?.pickupLocation || "";
  const waiverAck = !!state.fulfillment?.waiver?.acknowledged;

  const isValid = useMemo(() => {
    if (needsAddress) return !!addressConfirmed;
    if (method === "pickup") {
      if (!pickupLocation) return false;
      if (isThreePlus && !waiverAck) return false;
      return true;
    }
    return true;
  }, [
    needsAddress,
    addressConfirmed,
    method,
    pickupLocation,
    isThreePlus,
    waiverAck,
  ]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // --- handlers ---
  const handlePlaceSelect = useCallback(
    (formattedAddress, placeObj) => {
      setAddressText(formattedAddress);
      setAddressConfirmed(true);

      const { lat, lng } = extractLatLngFromPlace(placeObj);
      const km =
        originLatLng && Number.isFinite(lat) && Number.isFinite(lng)
          ? haversineKm(originLatLng, { lat, lng })
          : 0;

      setAddress({
        line1: formattedAddress,
        geo: { lat, lng },
        distanceKm: km ? Number(km.toFixed(1)) : 0,
      });

      setLocality(getLocality(placeObj));
    },
    [originLatLng, setAddress]
  );

  const handleAddressTextChange = useCallback(
    (txt) => {
      setAddressText(txt);
      setAddressConfirmed(false);
      setLocality("");
      if (!txt) {
        setAddress({ distanceKm: 0, geo: { lat: null, lng: null } });
      }
    },
    [setAddress]
  );

  const handleFulfillmentChange = useCallback(
    (_e, next) => {
      if (!next) return;
      if (next === method) return;

      if (next === "pickup") {
        // switching to pickup: clear delivery info and setup
        setFulfillment({
          method: "pickup",
          setupSelected: false,
          address: { line1: "", geo: { lat: null, lng: null }, distanceKm: 0 },
        });
        setAddressText("");
        setAddressConfirmed(false);
        setLocality("");

        // if only one pickup location, auto-select it for faster UX
        if (
          !state.fulfillment?.pickupLocation &&
          pickupLocations.length === 1
        ) {
          setPickupLocation(pickupLocations[0]);
        }
      } else {
        // switching to delivery
        setFulfillment({ method: "delivery" });
      }
    },
    [
      method,
      setFulfillment,
      setPickupLocation,
      pickupLocations,
      state.fulfillment?.pickupLocation,
    ]
  );

  const toggleSetup = useCallback(
    (checked) => {
      setFulfillment({ setupSelected: !!checked, method: "delivery" }); // setup implies delivery
    },
    [setFulfillment]
  );

  // pickup handlers
  const handleSelectPickupLocation = useCallback(
    (val) => {
      setPickupLocation(val || "");
    },
    [setPickupLocation]
  );

  const handleToggleWaiver = useCallback(
    (ack) => {
      setWaiver({
        acknowledged: !!ack,
        mustAcknowledgeForPickup3Tiers: isThreePlus,
        acknowledgedAt: ack ? new Date().toISOString() : null,
        messageVersion: "v1",
      });
    },
    [setWaiver, isThreePlus]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          justifyContent: "space-between",
          mb: 1.5,
        }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Fulfillment
        </Typography>

        <ToggleButtonGroup
          value={method}
          exclusive
          onChange={handleFulfillmentChange}
          size="small"
          color="primary">
          <ToggleButton value="delivery">Delivery</ToggleButton>
          <ToggleButton value="pickup">Pickup</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {method === "delivery" ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DeliveryCard
                config={dcfg}
                setupSelected={setupSelected}
                addressText={addressText}
                locality={locality}
                distanceKm={distanceKm}
                deliveryFee={deliveryFee}
                addressConfirmed={addressConfirmed}
                onAddressTextChange={handleAddressTextChange}
                onPlaceSelect={handlePlaceSelect}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SetupCard
                config={scfg}
                setupSelected={setupSelected}
                isInsideIncludedLocality={isInsideIncludedLocality}
                baseDelivery={baseDelivery}
                deliveryFee={deliveryFee}
                onToggleSetup={toggleSetup}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <TotalsBar
            method={method}
            setupSelected={setupSelected}
            setupFee={setupFee}
            billedDelivery={billedDelivery}
            setupTravelOverage={setupTravelOverage}
            total={total}
            isInsideIncludedLocality={isInsideIncludedLocality}
          />
        </>
      ) : (
        <>
          <PickupCard
            pickupLocations={pickupLocations}
            pickupLocation={pickupLocation}
            isThreePlus={isThreePlus}
            waiverAck={waiverAck}
            onSelectPickupLocation={handleSelectPickupLocation}
            onToggleWaiver={handleToggleWaiver}
          />

          <Divider sx={{ my: 2 }} />

          <TotalsBar
            method={method}
            setupSelected={false}
            setupFee={0}
            billedDelivery={0}
            setupTravelOverage={0}
            total={0}
            isInsideIncludedLocality
          />
        </>
      )}
    </Box>
  );
}
